import { Suspense, lazy, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { Toaster } from "react-hot-toast";
import { getTrainingInputSchema } from "../../../schema";
import type { AccessToken, Prediction, Version } from "../../../types";
import {
  PlaygroundContext,
  type PlaygroundContextValue,
} from "../../api-playground/context";
import { useCreateTrainingForm, type TrainingAction } from "../shared";

const TrainingForm = lazy(() => import("../training-form"));

export default function VLLMTraining({
  authenticated,
  avatar,
  destinations,
  modelInputSettings,
  owner,
  requiresCredit,
  token,
  training,
  trainingVersion,
  version,
}: {
  authenticated: boolean;
  avatar: string;
  destinations?: { name: string; username: string }[];
  modelInputSettings?: { hidden: string[] };
  owner: string;
  requiresCredit: boolean;
  token: AccessToken | null;
  training?: Prediction;
  trainingVersion?: Version;
  version: Version;
}) {
  const trainingInputSchema = getTrainingInputSchema(version._extras);

  const action: TrainingAction =
    training && trainingVersion ? "update" : "create";

  const destination = trainingVersion
    ? `${trainingVersion._extras.model.owner}/${trainingVersion._extras.model.name}`
    : null;

  const formHeading = useMemo(() => {
    if (action === "create") return "Create training";
    if (action === "update" && destination) {
      return `Retrain ${destination}`;
    }

    return "Create training";
  }, [action, destination]);

  const form = useCreateTrainingForm({
    action,
    training,
    trainingVersion,
    version,
  });

  const context: PlaygroundContextValue = {
    elementVisibility: {
      tweakButton: false,
    },
    features: {},
    hideAdvancedInputs: false,
    isAuthenticated: true,
    model: version._extras.model,
    modelInputSettings: modelInputSettings ?? { hidden: [] },
    modelStatus: null,
    permissions: {
      create_example: false,
      debug: false,
      delete: false,
      edit_featured_inputs: false,
      report: version._extras.model.visibility === "public",
      run: false,
      share: true,
      tweak: true,
    },
    requiresCredit,
    renderMode: "default",
    token: token ?? null,
    usesVersionlessApi: false,
    version,
  };

  return (
    <PlaygroundContext.Provider value={context}>
      <Toaster />
      <div className="flex flex-col md:flex-row gap-8">
        <div className="w-full md:max-w-2xl lg:flex-shrink-0">
          <Suspense>
            <FormProvider {...form}>
              <TrainingForm
                trainingVersion={trainingVersion}
                version={version}
                authenticated={authenticated}
                owner={owner}
                avatar={avatar}
                token={token}
                destinations={destinations}
                action={action}
                properties={trainingInputSchema?.properties || {}}
                showTabs={false}
              />
            </FormProvider>
          </Suspense>
        </div>
      </div>
    </PlaygroundContext.Provider>
  );
}
